import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import useRealtimeEvents from '../hooks/useRealtimeEvents';
import { API_URL as API } from '../lib/api';

const NotificationsContext = createContext(null);

const NOTIF_EVENTS = new Set(['notification_created']);

export function NotificationsProvider({ children }) {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useRealtimeEvents(NOTIF_EVENTS, (msg) => {
    if (!user?.user_id || msg.user_id !== user.user_id) return;
    const n = msg.notification;
    if (!n?.notification_id) return;
    setNotifications((prev) => {
      if (prev.some((x) => x.notification_id === n.notification_id)) return prev;
      return [n, ...prev];
    });
  }, { enabled: Boolean(token && user?.user_id) });

  const markAsRead = useCallback(async (notificationId) => {
    if (!token || !notificationId) return;
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === notificationId ? { ...n, read: true } : n)),
      );
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  }, [token]);

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    await fetch(`${API}/notifications/read-all`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [token]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const value = useMemo(() => ({
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  }), [notifications, loading, unreadCount, fetchNotifications, markAsRead, markAllAsRead]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return ctx;
}

export default NotificationsContext;
